import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import { grnService } from '../../services/grnService';
import { GRNListDTO, GRNApprovalStatus } from '../../types';
import './GRNPage.css';

const APPROVAL_BADGE: Record<GRNApprovalStatus, string> = {
  Pending:          'ims-badge ims-badge-pending',
  PendingApproval:  'ims-badge ims-badge-pending',
  PartiallyApproved:'ims-badge ims-badge-partial',
  Approved:         'ims-badge ims-badge-approved',
  Rejected:         'ims-badge ims-badge-rejected',
};

const GRNApprovalsPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [items,   setItems]   = useState<GRNListDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await grnService.getGRNs(1, 500);
      const payload = res.data?.data;
      const all: GRNListDTO[] = payload?.data ?? [];
      // Show everything that hasn't been fully approved
      setItems(all.filter(g => g.status !== 'Completed'));
    } catch {
      setError(t('grn.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  const pendingCount         = items.filter(g => g.approvalStatus === 'Pending').length;
  const pendingApprovalCount = items.filter(g => g.approvalStatus === 'PendingApproval').length;
  const partialCount         = items.filter(g => g.approvalStatus === 'PartiallyApproved').length;
  const approvedCount        = items.filter(g => g.approvalStatus === 'Approved').length;
  const rejectedCount        = items.filter(g => g.approvalStatus === 'Rejected').length;

  return (
    <Layout>
      <div className="grn-page">

        {/* Header */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('grn.pendingApprovals')}</h1>
            <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, marginTop: 2 }}>
              {t('grn.pendingApprovalsDesc')}
            </p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={fetchAll} disabled={loading}>
              <RefreshCw size={14} strokeWidth={2} className={loading ? 'refresh-icon-spinning' : ''} />
              {t('common.refresh')}
            </button>
            <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/grn')}>
              {t('grn.backToList')}
            </button>
          </div>
        </div>

        {/* Stat chips */}
        {!loading && (
          <div className="ims-stats-row" style={{ marginBottom: 16 }}>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{items.length}</span>
                <span className="ims-stat-label">{t('grn.pendingApprovals')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{pendingCount}</span>
                <span className="ims-stat-label">{t('grn.approvalPending')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{pendingApprovalCount}</span>
                <span className="ims-stat-label">{t('grn.awaitingApproval')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{partialCount}</span>
                <span className="ims-stat-label">{t('grn.approvalPartiallyApproved')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{approvedCount}</span>
                <span className="ims-stat-label">{t('grn.approvalApproved')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{rejectedCount}</span>
                <span className="ims-stat-label">{t('grn.approvalRejected')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ margin: '0 0 16px' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="ims-loading" style={{ padding: '48px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '64px 24px',
            color: 'var(--ims-text-muted)', background: '#fff',
            borderRadius: 12, border: '1px solid var(--ims-border)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ims-text-main)', marginBottom: 6 }}>
              {t('grn.noPendingApprovals')}
            </div>
            <div style={{ fontSize: 13 }}>{t('grn.noPendingApprovalsDesc')}</div>
          </div>
        )}

        {/* GRN cards */}
        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map(item => (
              <div key={item.grnId} className="ims-card" style={{ margin: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 12, padding: '16px 20px',
                }}>
                  {/* Left: GRN info */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                        {item.grnNumber}
                      </span>
                      <span className={APPROVAL_BADGE[item.approvalStatus] ?? 'ims-badge ims-badge-pending'}>
                        {t(`grn.approval${item.approvalStatus}`)}
                      </span>
                      {item.grnType === 'WithPO' && (
                        <span className="ims-badge ims-badge-withpo">
                          {t('grn.typeBadgeWithPO')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ims-text-sub)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {item.supplierName && (
                        <span><strong>{t('grn.supplier')}:</strong> {item.supplierName}</span>
                      )}
                      {item.warehouseName && (
                        <span><strong>{t('grn.warehouse')}:</strong> {item.warehouseName}</span>
                      )}
                      <span><strong>{t('grn.date')}:</strong> {new Date(item.grnDate).toLocaleDateString(langCode)}</span>
                      <span><strong>{t('grn.items')}:</strong> {item.itemCount}</span>
                    </div>
                    {(item.approvalStatus === 'Pending' || item.approvalStatus === 'PendingApproval') && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#0e7490', fontWeight: 500 }}>
                        {t('grn.awaitingApproval')}
                      </div>
                    )}
                    {item.approvalStatus === 'PartiallyApproved' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#7c3aed', fontWeight: 500 }}>
                        {t('grn.approvalPartiallyApproved')}
                      </div>
                    )}
                    {item.approvalStatus === 'Approved' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#15803d', fontWeight: 500 }}>
                        {t('grn.approvalApproved')} — {t('grn.readyToComplete')}
                      </div>
                    )}
                    {item.approvalStatus === 'Rejected' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b', fontWeight: 500 }}>
                        {t('grn.approvalRejected')}
                      </div>
                    )}
                  </div>

                  {/* Right: actions */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      className="ims-btn ims-btn-secondary"
                      style={{ fontSize: 13 }}
                      onClick={() => navigate(`/grn/${item.grnId}`)}
                    >
                      {item.approvalStatus === 'Approved'
                        ? t('grn.completeGRN')
                        : t('common.view')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </Layout>
  );
};

export default GRNApprovalsPage;
